import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import React from 'react';
import type { CodeReviewAction, TransactionCode } from '../../interface/transactionCodes';
import {
  CODE_FOCUS_BAR,
  CODE_FOCUS_BG,
  CODE_FOCUS_RADIUS,
  CODE_ACTION_GHOST_INK,
  CODE_ROW_HOVER_BG,
  CODE_ROW_PAD_X,
  CODE_ROW_PAD_Y,
  CODE_ROW_RADIUS,
  DRAG_TARGET_LINE,
  INK,
  INK_MUTED,
  REVIEW_ICON_BG,
  REVIEW_ICON_SIZE,
  REVIEW_RING_SIZE,
  REVIEW_RING_STROKE,
  ROW_H,
  CHIP_BORDER
} from '../../styles/tokens';
import { CodeActions } from './CodeActions';
import { CodePill } from './CodePill';
import { ProvenanceIcons } from './ProvenanceIcons';

export type CodeRowProps = {
  readonly code: TransactionCode;
  readonly grabber: 'inert' | 'active' | 'none';
  readonly dropIndicator?: 'before' | 'after';
  readonly expanded?: boolean;
  readonly onToggle?: () => void;
  readonly onAction?: (action: CodeReviewAction) => void;
  readonly hint?: CodeReviewAction;
  readonly onGrabberDragStart?: (event: React.DragEvent) => void;
  readonly onDragOver?: (event: React.DragEvent) => void;
  readonly onDrop?: (event: React.DragEvent) => void;
  readonly onDragEnd?: (event: React.DragEvent) => void;
};

const ELLIPSIS: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

export const CodeRow: React.FC<CodeRowProps> = ({
  code,
  grabber,
  dropIndicator,
  expanded,
  onToggle,
  onAction,
  hint,
  onGrabberDragStart,
  onDragOver,
  onDrop,
  onDragEnd
}) => {
  const actionsId = code.id + '-actions';
  const status = (
    <Tooltip
      label={code.reviewState === 'accepted' ? 'Accepted' : 'Needs review — review status will be added in a later release'}
      placement="top"
      openDelay={300}
    >
      <Box
        as="span"
        role="img"
        aria-label={code.reviewState === 'accepted' ? 'Accepted' : 'Needs review'}
        display="inline-flex"
        color={code.reviewState === 'accepted' ? INK_MUTED : CODE_ACTION_GHOST_INK}
        w={code.reviewState === 'review' ? REVIEW_ICON_SIZE : undefined}
        h={code.reviewState === 'review' ? REVIEW_ICON_SIZE : undefined}
        flexShrink={code.reviewState === 'review' ? 0 : undefined}
        borderRadius={code.reviewState === 'review' ? '50%' : undefined}
        border={code.reviewState === 'review' ? '1px solid' : undefined}
        borderColor={code.reviewState === 'review' ? CHIP_BORDER : undefined}
        bg={code.reviewState === 'review' ? REVIEW_ICON_BG : undefined}
        alignItems={code.reviewState === 'review' ? 'center' : undefined}
        justifyContent={code.reviewState === 'review' ? 'center' : undefined}
      >
        {code.reviewState === 'accepted' ? (
          <BiomeIcon iconName={IconName.Check_Circle} />
        ) : (
          <svg
            data-testid="review-ring"
            width={REVIEW_RING_SIZE}
            height={REVIEW_RING_SIZE}
            viewBox="0 0 10 10"
            aria-hidden="true"
          >
            <circle cx="5" cy="5" r="4" fill="none" stroke="currentColor" strokeWidth={REVIEW_RING_STROKE} />
          </svg>
        )}
      </Box>
    </Tooltip>
  );
  const description = code.description !== undefined ? (
    <Tooltip label={code.description} placement="top" openDelay={300}>
      <Text
        as="span"
        id={code.id + '-description'}
        flex="1"
        minW={0}
        fontSize="12px"
        color={INK}
        style={ELLIPSIS}
        data-testid="code-description"
      >
        {code.description}
      </Text>
    </Tooltip>
  ) : (
    <Text as="span" flex="1" minW={0} data-testid="code-description-pending" />
  );
  const codeContent = onToggle ? (
    <Flex
      role="button"
      tabIndex={0}
      aria-expanded={expanded === true}
      aria-controls={expanded ? actionsId : undefined}
      aria-describedby={code.description !== undefined ? code.id + '-description' : undefined}
      aria-label={'Review actions for ' + code.code}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      flex="1"
      minW={0}
      align="center"
      gap="8px"
      cursor="pointer"
    >
      {status}
      <CodePill codeType={code.codeType} code={code.code} />
      {description}
    </Flex>
  ) : (
    <>
      {status}
      <CodePill codeType={code.codeType} code={code.code} />
      {description}
    </>
  );

  return (
    <Box
      bg={expanded ? CODE_FOCUS_BG : undefined}
      borderRadius={expanded ? CODE_FOCUS_RADIUS : undefined}
      boxShadow={expanded ? 'inset 3px 0 0 0 ' + CODE_FOCUS_BAR : undefined}
      data-testid="code-row"
      data-code-id={code.id}
      data-drop-indicator={dropIndicator}
      onDragOver={onDragOver}
      onDrop={onDrop}
      position="relative"
      _before={
        dropIndicator
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              height: '2px',
              bg: DRAG_TARGET_LINE,
              top: dropIndicator === 'before' ? 0 : undefined,
              bottom: dropIndicator === 'after' ? 0 : undefined
            }
          : undefined
      }
    >
      <Flex
        align="center"
        gap="8px"
        minH={ROW_H}
        px={CODE_ROW_PAD_X}
        py={CODE_ROW_PAD_Y}
        borderRadius={CODE_ROW_RADIUS}
        transition="background-color 0.12s"
        _hover={expanded ? undefined : { bg: CODE_ROW_HOVER_BG }}
      >
        {grabber === 'active' && (
          <Box as="span" role="button" aria-label="Drag to reorder" tabIndex={0} draggable onDragStart={onGrabberDragStart} onDragEnd={onDragEnd} cursor="grab" color={INK_MUTED} display="inline-flex">
            <BiomeIcon iconName={IconName.Gripper} />
          </Box>
        )}
        {grabber === 'inert' && (
          <Box as="span" role="button" aria-label="Drag to reorder" aria-disabled="true" tabIndex={-1} title="Reordering is not available here" cursor="not-allowed" color={INK_MUTED} display="inline-flex">
            <BiomeIcon iconName={IconName.Gripper} />
          </Box>
        )}
        {codeContent}
        {onToggle ? (
          <Box display="inline-flex" cursor="pointer" onClick={onToggle}>
            <ProvenanceIcons provenance={code.provenance} />
          </Box>
        ) : (
          <ProvenanceIcons provenance={code.provenance} />
        )}
      </Flex>
      {expanded && <CodeActions id={actionsId} onAction={onAction} hint={hint} />}
    </Box>
  );
};
